import type { AuthRole } from "@/contexts/AuthContext";

export type AnnouncementAudienceScope =
  | "all_signed_in"
  | "admins_only"
  | "members_only"
  | "moderators_only";
export type AnnouncementDisplayStyle = "banner" | "modal";

export interface KindredAnnouncement {
  id: string;
  title: string;
  body: string;
  cta_label?: string | null;
  cta_url_or_route?: string | null;
  audience_scope: AnnouncementAudienceScope;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active: boolean;
  display_style: AnnouncementDisplayStyle;
  dismissible: boolean;
  updated_at?: string | null;
}

export const announcementDismissKey = (userId: string, announcement: KindredAnnouncement) =>
  `kindred:announcement:dismissed:${userId}:${announcement.id}:${announcement.updated_at ?? "base"}`;

export const announcementMatchesRole = (
  audienceScope: AnnouncementAudienceScope,
  role: AuthRole,
) => {
  switch (audienceScope) {
    case "admins_only":
      return role === "admin";
    case "moderators_only":
      return role === "admin" || role === "moderator";
    case "members_only":
      return role === "user";
    case "all_signed_in":
    default:
      return true;
  }
};

export const isAnnouncementInWindow = (announcement: KindredAnnouncement, now = new Date()) => {
  const startsAt = announcement.starts_at ? new Date(announcement.starts_at) : null;
  const endsAt = announcement.ends_at ? new Date(announcement.ends_at) : null;

  if (startsAt && startsAt.getTime() > now.getTime()) {
    return false;
  }

  if (endsAt && endsAt.getTime() < now.getTime()) {
    return false;
  }

  return announcement.is_active;
};

export const pickActiveAnnouncement = (
  announcements: KindredAnnouncement[],
  role: AuthRole,
) =>
  announcements.find(
    (announcement) =>
      isAnnouncementInWindow(announcement) &&
      announcementMatchesRole(announcement.audience_scope, role),
  ) ?? null;
